/**
 * Role-based access matrix, shared by server guards and UI route guards.
 * Stage owners write only the steps of their own departments (checked per step on the
 * server); the planner can write any step and owns plans; management only reads.
 */
import type { Role } from './types';

export const PERMISSIONS = [
  'project.view',
  'project.edit',
  'step.update',
  'step.updateAny',
  'dashboard.view',
  'reports.view',
  'display.view',
  'admin.manage',
  'audit.view',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

const MATRIX: Record<Role, readonly Permission[]> = {
  owner: ['project.view', 'step.update'],
  planner: [
    'project.view',
    'project.edit',
    'step.update',
    'step.updateAny',
    'dashboard.view',
    'reports.view',
    'display.view',
  ],
  mgmt: ['project.view', 'dashboard.view', 'reports.view', 'display.view'],
  admin: ['project.view', 'dashboard.view', 'reports.view', 'display.view', 'admin.manage', 'audit.view'],
};

export function can(role: Role, permission: Permission): boolean {
  return MATRIX[role].includes(permission);
}

/** True when any of the user's roles grants the permission. Returns the granting role. */
export function grantingRole(roles: readonly Role[], permission: Permission): Role | null {
  return roles.find((r) => can(r, permission)) ?? null;
}

/** The one home screen each role lands on after the PIN. */
export const HOME_PATH: Record<Role, string> = {
  owner: '/work',
  planner: '/projects',
  mgmt: '/dashboard',
  admin: '/admin',
};
